
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../dashboard/components/Header';

interface UserProfile {
  user_id: number;
  user_name_cn: string;
  user_name_en: string;
  user_status: number;
  entry_date: string;
  contract_expiry_date: string;
  coze_sys_uuid: string;
  userid: string;
  role: string;
  company_name: string;
  dept_name: string;
  employee_number: string;
  phone_number: string;
  identity_code: string;
  position: string;
  direct_super: string;
}

export default function Profile() {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [userRole, setUserRole] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // 获取用户基本信息
    setUserName(localStorage.getItem('userName') || '');
    setUserRole(localStorage.getItem('userRole') || '');

    // 获取完整的用户资料
    const profileData = localStorage.getItem('userProfile');
    if (profileData) {
      try {
        setUserProfile(JSON.parse(profileData));
      } catch (error) {
        console.error('解析用户资料失败:', error);
      }
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '未设置';
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  const getStatusText = (status: number) => {
    return status === 1 ? '在职' : '离职';
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return '系统管理员';
      case 'manager': return '部门经理';
      case 'employee': return '员工';
      default: return role || '普通用户';
    }
  };

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header userName={userName} userRole={userRole} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header userName={userName} userRole={userRole} onLogout={handleLogout} />
      
      <main className="max-w-4xl mx-auto py-8 px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">个人资料</h1>
          <p className="text-gray-600">查看和管理您的个人信息</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* 用户头像和基本信息 */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-12">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-3xl font-bold text-white">
                  {(userProfile.user_name_cn || userProfile.user_name_en || '用户').charAt(0)}
                </span>
              </div>
              <div className="text-white">
                <h2 className="text-2xl font-bold mb-2">
                  {userProfile.user_name_cn || userProfile.user_name_en || '未设置姓名'}
                </h2>
                <p className="text-lg opacity-90 mb-1">{userProfile.position || '职位未设置'}</p>
                <p className="opacity-80">{userProfile.dept_name || '部门未设置'}</p>
                <div className="flex items-center mt-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    userProfile.user_status === 1 
                      ? 'bg-green-500 bg-opacity-20 text-green-100' 
                      : 'bg-red-500 bg-opacity-20 text-red-100'
                  }`}>
                    {getStatusText(userProfile.user_status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 详细信息 */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 基本信息 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <i className="ri-user-line text-indigo-600 mr-2"></i>
                  基本信息
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">用户ID</span>
                    <span className="font-medium">{userProfile.user_id}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">中文姓名</span>
                    <span className="font-medium">{userProfile.user_name_cn || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">英文姓名</span>
                    <span className="font-medium">{userProfile.user_name_en || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">手机号码</span>
                    <span className="font-medium">{userProfile.phone_number}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">工号</span>
                    <span className="font-medium">{userProfile.employee_number || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-600">身份识别码</span>
                    <span className="font-medium">{userProfile.identity_code || '未设置'}</span>
                  </div>
                </div>
              </div>

              {/* 工作信息 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <i className="ri-briefcase-line text-indigo-600 mr-2"></i>
                  工作信息
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">用户角色</span>
                    <span className="font-medium">{getRoleText(userProfile.role)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">公司名称</span>
                    <span className="font-medium">{userProfile.company_name || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">部门名称</span>
                    <span className="font-medium">{userProfile.dept_name || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">职位</span>
                    <span className="font-medium">{userProfile.position || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">直接上级</span>
                    <span className="font-medium">{userProfile.direct_super || '未设置'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-600">企业微信ID</span>
                    <span className="font-medium">{userProfile.userid || '未设置'}</span>
                  </div>
                </div>
              </div>

              {/* 时间信息 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <i className="ri-calendar-line text-indigo-600 mr-2"></i>
                  时间信息
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">入职时间</span>
                    <span className="font-medium">{formatDate(userProfile.entry_date)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-600">合同到期时间</span>
                    <span className="font-medium">{formatDate(userProfile.contract_expiry_date)}</span>
                  </div>
                </div>
              </div>

              {/* 系统信息 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                  <i className="ri-settings-line text-indigo-600 mr-2"></i>
                  系统信息
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">用户状态</span>
                    <span className={`font-medium ${
                      userProfile.user_status === 1 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {getStatusText(userProfile.user_status)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-600">Coze系统UUID</span>
                    <span className="font-medium text-xs">{userProfile.coze_sys_uuid || '未设置'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  返回工作台
                </button>
                <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  编辑资料
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
